import { VirtualDevice, type SimulationContext } from '../VirtualDevice';
import { ModbusMapper } from '../../modbus/ModbusMapper';
import { safeRandomUUID } from '@/core/types/ids';
import {
  ModbusFunctionCode,
  ModbusExceptionCode,
  type ModbusRequest,
  type ModbusResponse,
  type ModbusMapping,
  type ModbusDataArea
} from '../../modbus/ModbusTypes';

export class ModbusServerDevice extends VirtualDevice {
  private _mapper?: ModbusMapper;
  public get mapper(): ModbusMapper {
    if (!this._mapper) {
      this._mapper = new ModbusMapper();
      const rawMappings = this.props['mappings'] as unknown;
      if (Array.isArray(rawMappings)) {
        this._mapper.setMappings(rawMappings as ModbusMapping[]);
      }
    }
    return this._mapper;
  }
  private isOnline = true;
  
  override reset() {
    this.isOnline = Boolean(this.props['isOnline'] ?? true);
    
    if (this._mapper) {
      const rawMappings = this.props['mappings'] as unknown;
      if (Array.isArray(rawMappings)) {
        this._mapper.setMappings(rawMappings as ModbusMapping[]);
      } else {
        this._mapper.setMappings([]);
      }
    }
  }

  override update(_context: SimulationContext) {
    this.isOnline = Boolean(this.props['isOnline'] ?? true);
    const unitId = Number(this.props['unitId'] ?? 1);
    
    // Save to state for UI visualization
    this.state.isOnline = this.isOnline;
    this.state.unitId = unitId;
    
    const rawMappings = this.props['mappings'] as unknown;
    if (Array.isArray(rawMappings)) {
      this.mapper.setMappings(rawMappings as ModbusMapping[]);
    }
  }

  public handleRequest(request: ModbusRequest, context: SimulationContext): ModbusResponse {
    const unitId = Number(this.props['unitId'] ?? 1);
    
    if (!this.isOnline) {
      return this.createException(request, ModbusExceptionCode.ServerDeviceFailure, context);
    }
    
    if (request.unitId !== unitId) {
      // Modbus typically ignores requests for other unit IDs, but we can return timeout or ignore in simulator.
      // Here we simulate a failure because we are explicitly targeting this device and unitId didn't match.
      return this.createException(request, ModbusExceptionCode.ServerDeviceFailure, context);
    }

    try {
      switch (request.functionCode) {
        case ModbusFunctionCode.ReadCoils:
          return this.readArea(request, context, 'COIL');
        case ModbusFunctionCode.ReadDiscreteInputs:
          return this.readArea(request, context, 'DISCRETE_INPUT');
        case ModbusFunctionCode.ReadHoldingRegisters:
          return this.readArea(request, context, 'HOLDING_REGISTER');
        case ModbusFunctionCode.ReadInputRegisters:
          return this.readArea(request, context, 'INPUT_REGISTER');
        case ModbusFunctionCode.WriteSingleCoil:
          return this.writeSingle(request, context, 'COIL');
        case ModbusFunctionCode.WriteSingleRegister:
          return this.writeSingle(request, context, 'HOLDING_REGISTER');
        case ModbusFunctionCode.WriteMultipleCoils:
          return this.writeMultiple(request, context, 'COIL');
        case ModbusFunctionCode.WriteMultipleRegisters:
          return this.writeMultiple(request, context, 'HOLDING_REGISTER');
        default:
          return this.createException(request, ModbusExceptionCode.IllegalFunction, context);
      }
    } catch {
      return this.createException(request, ModbusExceptionCode.ServerDeviceFailure, context);
    }
  }

  private readArea(request: ModbusRequest, context: SimulationContext, area: ModbusDataArea): ModbusResponse {
    const quantity = request.quantity ?? 1;
    if (quantity < 1 || quantity > 125) {
      return this.createException(request, ModbusExceptionCode.IllegalDataValue, context);
    }

    const data: number[] = [];
    for (let i = 0; i < quantity; i++) {
      const val = this.mapper.read(context.host, area, request.startAddress + i);
      if (val === null) {
        return this.createException(request, ModbusExceptionCode.IllegalDataAddress, context);
      }
      data.push(val);
    }

    return {
      requestId: request.id,
      unitId: request.unitId,
      functionCode: request.functionCode,
      isException: false,
      data,
      timestamp: context.simulationTimeMs
    };
  }

  private writeSingle(request: ModbusRequest, context: SimulationContext, area: ModbusDataArea): ModbusResponse {
    if (!request.values || request.values.length < 1) {
      return this.createException(request, ModbusExceptionCode.IllegalDataValue, context);
    }

    const val = request.values[0] as number;
    const success = this.mapper.write(context.host, area, request.startAddress, val);
    
    if (!success) {
      return this.createException(request, ModbusExceptionCode.IllegalDataAddress, context);
    }

    return {
      requestId: request.id,
      unitId: request.unitId,
      functionCode: request.functionCode,
      isException: false,
      timestamp: context.simulationTimeMs
    };
  }

  private writeMultiple(request: ModbusRequest, context: SimulationContext, area: ModbusDataArea): ModbusResponse {
    const quantity = request.quantity ?? 1;
    if (!request.values || request.values.length < quantity) {
      return this.createException(request, ModbusExceptionCode.IllegalDataValue, context);
    }

    // Check all addresses first (simulate atomic transaction check)
    for (let i = 0; i < quantity; i++) {
      const mapCheck = this.mapper.getMappings().find(m => m.area === area && m.modbusAddress === request.startAddress + i);
      if (!mapCheck) {
        return this.createException(request, ModbusExceptionCode.IllegalDataAddress, context);
      }
    }

    // Write all
    for (let i = 0; i < quantity; i++) {
      this.mapper.write(context.host, area, request.startAddress + i, request.values[i] as number);
    }

    return {
      requestId: request.id,
      unitId: request.unitId,
      functionCode: request.functionCode,
      isException: false,
      timestamp: context.simulationTimeMs
    };
  }

  private createException(request: ModbusRequest, code: ModbusExceptionCode, context: SimulationContext): ModbusResponse {
    return {
      requestId: request.id,
      unitId: request.unitId,
      functionCode: request.functionCode,
      isException: true,
      exceptionCode: code,
      timestamp: context.simulationTimeMs
    };
  }
}

export interface ModbusClientDeviceState extends Record<string, unknown> {
  lastResponse: ModbusResponse | null;
  isRequesting: boolean;
  lastTrigger?: boolean;
}

export class ModbusClientDevice extends VirtualDevice<ModbusClientDeviceState> {
  private activeRequest: ModbusRequest | null = null;
  private requestStartTime = 0;
  
  override reset() {
    this.activeRequest = null;
    this.state.lastResponse = null;
    this.state.isRequesting = false;
  }

  override update(context: SimulationContext) {
    const trigger = Boolean(this.props['trigger']);
    
    // Simple edge detection for trigger
    if (trigger && !this.state.lastTrigger) {
      this.sendRequest(context);
    }
    this.state.lastTrigger = trigger;

    if (this.activeRequest) {
      const serverId = String(this.props['serverId'] || '');
      const server = context.process.getDevice(serverId);
      
      if (!server || server.type !== 'modbus-server') {
        this.finishRequest({
          requestId: this.activeRequest.id,
          unitId: this.activeRequest.unitId,
          functionCode: this.activeRequest.functionCode,
          isException: true,
          exceptionCode: ModbusExceptionCode.ServerDeviceFailure,
          timestamp: context.simulationTimeMs
        }, context);
      } else {
        // Simulate delay (e.g. 20ms)
        const delay = Number(this.props['delayMs'] ?? 20);
        if (context.simulationTimeMs - this.requestStartTime >= delay) {
          const response = (server as ModbusServerDevice).handleRequest(this.activeRequest, context);
          this.finishRequest(response, context);
        }
      }
    }
  }

  private sendRequest(context: SimulationContext) {
    if (this.activeRequest) return; // Busy

    // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-assignment
    const functionCode: ModbusFunctionCode = Number(this.props['functionCode'] ?? 3);
    const request: ModbusRequest = {
      id: safeRandomUUID(),
      unitId: Number(this.props['unitId'] ?? 1),
      functionCode,
      startAddress: Number(this.props['startAddress'] ?? 0),
      quantity: Number(this.props['quantity'] ?? 1),
      timestamp: context.simulationTimeMs
    };

    if (functionCode === ModbusFunctionCode.WriteSingleCoil || functionCode === ModbusFunctionCode.WriteSingleRegister || 
        functionCode === ModbusFunctionCode.WriteMultipleCoils || functionCode === ModbusFunctionCode.WriteMultipleRegisters) {
      const rawValues = this.props['writeValues'];
      if (Array.isArray(rawValues)) {
        request.values = rawValues.map(v => Number(v));
      } else {
        request.values = [Number(rawValues ?? 0)];
      }
    }

    this.activeRequest = request;
    this.requestStartTime = context.simulationTimeMs;
    this.state.isRequesting = true;
    
    context.alarms.logEvent(context.simulationTimeMs, 'SYSTEM', this.id, `Modbus Request sent to server: ${this.props['serverId']}`);
  }

  private finishRequest(response: ModbusResponse, context: SimulationContext) {
    this.state.lastResponse = response;
    this.activeRequest = null;
    this.state.isRequesting = false;

    if (response.isException) {
      context.alarms.logEvent(context.simulationTimeMs, 'SYSTEM', this.id, `Modbus Request failed. Exception: ${response.exceptionCode}`);
    } else {
      context.alarms.logEvent(context.simulationTimeMs, 'SYSTEM', this.id, `Modbus Request successful.`);
    }
  }
}
